import React, { useState } from 'react';
import {
  Box, Container, Paper, Typography, Card, CardContent, Chip, Button, IconButton, Tooltip, List,
  ListItem, ListItemText, ListItemIcon, Avatar, LinearProgress, Tab, Tabs, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, Alert, Snackbar, styled, alpha, useTheme, useMediaQuery
} from '@mui/material';
import {
  Star, AutoAwesome, CreditCard, Receipt, Cancel, Upgrade, Check, Close, Download,
  Refresh, Warning, Payment, History, Security, Notifications, Help
} from '@mui/icons-material';
import { GridLegacy as Grid } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

// Types
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  billing: 'monthly' | 'yearly';
  features: string[];
  limits: {
    aiChats: number | 'unlimited';
    reports: number | 'unlimited';
    compatibility: boolean;
    priority: boolean;
  };
  popular?: boolean;
}

interface UserSubscription {
  id: string;
  planId: string;
  status: 'active' | 'cancelled' | 'past_due' | 'trialing';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Date;
  usage: {
    aiChats: number;
    reports: number;
    lastUsage: Date;
  };
}

interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal';
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface Invoice {
  id: string;
  date: Date;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  downloadUrl: string;
  description: string;
}

// Custom theme colors (using your brand palette)
const brandColors = {
  primary: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
  },
  secondary: {
    main: '#8B5CF6',
    light: '#A78BFA',
    dark: '#6D28D9',
  },
  accent: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
  }
};

// Styled Components
const SubscriptionContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
  position: 'relative',
  overflow: 'hidden',
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
  borderRadius: '24px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const PlanCard = styled(Card)<{ featured?: boolean; current?: boolean }>(({ theme, featured, current }) => ({
  background: current
    ? `linear-gradient(135deg, ${alpha(brandColors.secondary.main, 0.2)}, ${alpha(brandColors.secondary.main, 0.1)})`
    : featured
      ? `linear-gradient(135deg, ${alpha(brandColors.primary.main, 0.2)}, ${alpha(brandColors.primary.main, 0.1)})`
      : `linear-gradient(135deg, ${alpha('#1E293B', 0.8)}, ${alpha('#1E293B', 0.6)})`,
  backdropFilter: 'blur(20px)',
  border: current
    ? `2px solid ${brandColors.secondary.main}`
    : featured
      ? `2px solid ${brandColors.primary.main}`
      : `1px solid ${alpha('#1E293B', 0.3)}`,
  borderRadius: '20px',
  position: 'relative',
  transition: 'all 0.3s ease',
  transform: featured ? 'scale(1.05)' : 'none',
  '&:hover': {
    transform: featured ? 'scale(1.08)' : 'scale(1.02)',
    borderColor: featured ? brandColors.primary.light : brandColors.primary.main,
  },
  ...(featured && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: `linear-gradient(90deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
      borderRadius: '20px 20px 0 0',
    }
  })
}));

const TabPanel = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getStatusColor = (s: string) => {
    switch (s) {
      case 'active': return '#10B981';
      case 'trialing': return '#F59E0B';
      case 'cancelled': return '#64748B';
      case 'past_due': return '#EF4444';
      default: return '#64748B';
    }
  };

  return {
    background: alpha(getStatusColor(status), 0.2),
    color: getStatusColor(status),
    fontWeight: 600,
    textTransform: 'capitalize'
  };
});

const SubscriptionManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  console.log(isMobile);
  const [activeTab, setActiveTab] = useState(0);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const { isRTL } = useLanguage();
  const { t } = useTranslation();

  // Mock data - in real app, this would come from API
  const availablePlans: SubscriptionPlan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      currency: 'USD',
      billing: 'monthly',
      features: [
        'subscription.feature.dailyHoroscopes',
        'subscription.feature.basicCompatibility',
        'subscription.feature.weeklyForecasts',
        'subscription.feature.limitedAiQuestions'
      ],
      limits: {
        aiChats: 3,
        reports: 1,
        compatibility: false,
        priority: false
      }
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 4.99,
      currency: 'USD',
      billing: 'monthly',
      features: [
        'subscription.feature.everythingInFree',
        'subscription.feature.unlimitedAiConversations',
        'subscription.feature.detailedBirthChart',
        'subscription.feature.personalizedDailyInsights',
        'subscription.feature.transitNotifications',
        'subscription.feature.premiumCompatibilityReports'
      ],
      limits: {
        aiChats: 'unlimited',
        reports: 'unlimited',
        compatibility: true,
        priority: false
      },
      popular: true
    },
    {
      id: 'elite',
      name: 'Elite',
      price: 9.99,
      currency: 'USD',
      billing: 'monthly',
      features: [
        'subscription.feature.everythingInPro',
        'subscription.feature.priorityAiResponses',
        'subscription.feature.advancedCompatibility',
        'subscription.feature.exclusivePredictiveInsights',
        'subscription.feature.personalAstrologerConsultations',
        'subscription.feature.earlyAccess'
      ],
      limits: {
        aiChats: 'unlimited',
        reports: 'unlimited',
        compatibility: true,
        priority: true
      }
    }
  ];

  const currentSubscription: UserSubscription = {
    id: 'sub_123',
    planId: 'pro',
    status: 'active',
    currentPeriodStart: new Date('2025-06-01'),
    currentPeriodEnd: new Date('2025-07-01'),
    cancelAtPeriodEnd: false,
    usage: {
      aiChats: 42,
      reports: 8,
      lastUsage: new Date('2025-06-27')
    }
  };

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'pm_1',
      type: 'card',
      last4: '4242',
      brand: 'visa',
      expiryMonth: 12,
      expiryYear: 2027,
      isDefault: true
    },
    {
      id: 'pm_2',
      type: 'paypal',
      isDefault: false
    }
  ];

  const invoices: Invoice[] = [
    {
      id: 'inv_1',
      date: new Date('2025-06-01'),
      amount: 4.99,
      currency: 'USD',
      status: 'paid',
      downloadUrl: '#',
      description: 'Pro Plan - Monthly'
    },
    {
      id: 'inv_2',
      date: new Date('2025-05-01'),
      amount: 4.99,
      currency: 'USD',
      status: 'paid',
      downloadUrl: '#',
      description: 'Pro Plan - Monthly'
    },
    {
      id: 'inv_3',
      date: new Date('2025-04-01'),
      amount: 4.99,
      currency: 'USD',
      status: 'paid',
      downloadUrl: '#',
      description: 'Pro Plan - Monthly'
    }
  ];

  const currentPlan = availablePlans.find(plan => plan.id === currentSubscription.planId);
  const daysUntilRenewal = Math.ceil((currentSubscription.currentPeriodEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleUpgrade = (planId: string) => {
    setUpgradeDialogOpen(false);
    setSnackbarMessage(`Successfully upgraded to ${availablePlans.find(p => p.id === planId)?.name} plan!`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleCancelSubscription = () => {
    setCancelDialogOpen(false);
    setSnackbarMessage('Subscription cancelled. You can continue using Pro features until your current period ends.');
    setSnackbarSeverity('warning');
    setSnackbarOpen(true);
  };

  const handleReactivate = () => {
    setSnackbarMessage('Subscription reactivated successfully!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const getUsagePercentage = (used: number, limit: number | 'unlimited') => {
    if (limit === 'unlimited') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage < 70) return '#10B981';
    if (percentage < 90) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <SubscriptionContainer>
      {/* Animated Background */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            <Star sx={{ fontSize: 6, color: 'white' }} />
          </Box>
        ))}
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        {/* Header */}
        <HeaderCard>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                {t('subscription.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                {t('subscription.subtitle')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Help />}
                sx={{
                  borderColor: brandColors.primary.main,
                  color: brandColors.primary.main,
                  '&:hover': {
                    borderColor: brandColors.primary.light,
                    background: alpha(brandColors.primary.main, 0.1)
                  }
                }}
              >
                {t('help_support')}
              </Button>
            </Box>
          </Box>

          {/* Current Plan Overview */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Box sx={{
                background: alpha(brandColors.secondary.main, 0.1),
                border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`,
                borderRadius: 3,
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{
                    background: `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                    width: 48,
                    height: 48
                  }}>
                    <AutoAwesome sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                      {t(`subscription.plan.${currentSubscription.planId}`)} Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <StatusChip label={currentSubscription.status} status={currentSubscription.status} size="small" />
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        {t('subscription.renewsIn', { days: daysUntilRenewal })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 3 }}>
                  ${currentPlan?.price}/{t(`subscription.billing.${currentPlan?.billing}`)} • {t('subscription.nextBilling', { date: currentSubscription.currentPeriodEnd.toLocaleDateString() })}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<Upgrade />}
                    onClick={() => setUpgradeDialogOpen(true)}
                    sx={{
                      background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`,
                      }
                    }}
                  >
                    {t('subscription.upgradePlan')}
                  </Button>

                  {!currentSubscription.cancelAtPeriodEnd ? (
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={() => setCancelDialogOpen(true)}
                      sx={{
                        borderColor: '#EF4444',
                        color: '#EF4444',
                        '&:hover': {
                          borderColor: '#F87171',
                          background: alpha('#EF4444', 0.1)
                        }
                      }}
                    >
                      {t('subscription.cancelPlan')}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleReactivate}
                      sx={{
                        borderColor: '#10B981',
                        color: '#10B981',
                        '&:hover': {
                          borderColor: '#34D399',
                          background: alpha('#10B981', 0.1)
                        }
                      }}
                    >
                      {t('subscription.reactivate')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{
                background: alpha('#1E293B', 0.6),
                border: `1px solid ${alpha(brandColors.accent.main, 0.3)}`,
                borderRadius: 3,
                p: 3
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                  {t('subscription.usageThisMonth')}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                      {t('subscription.aiConversations')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      {currentSubscription.usage.aiChats} / {currentPlan?.limits.aiChats === 'unlimited' ? t('subscription.infinity') : currentPlan?.limits.aiChats}
                    </Typography>
                  </Box>
                  {currentPlan?.limits.aiChats !== 'unlimited' && (
                    <LinearProgress
                      variant="determinate"
                      value={getUsagePercentage(currentSubscription.usage.aiChats, currentPlan?.limits.aiChats || 0)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha('#1E293B', 0.6),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getUsageColor(getUsagePercentage(currentSubscription.usage.aiChats, currentPlan?.limits.aiChats || 0)),
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                      {t('subscription.reportsGenerated')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      {currentSubscription.usage.reports} / {currentPlan?.limits.reports === 'unlimited' ? t('subscription.infinity') : currentPlan?.limits.reports}
                    </Typography>
                  </Box>
                  {currentPlan?.limits.reports !== 'unlimited' && (
                    <LinearProgress
                      variant="determinate"
                      value={getUsagePercentage(currentSubscription.usage.reports, currentPlan?.limits.reports || 0)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha('#1E293B', 0.6),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getUsageColor(getUsagePercentage(currentSubscription.usage.reports, currentPlan?.limits.reports || 0)),
                          borderRadius: 3,
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </HeaderCard>

        {/* Tabs Navigation */}
        <Paper sx={{
          background: `linear-gradient(135deg, 
            ${alpha('#1E293B', 0.8)} 0%, 
            ${alpha(brandColors.primary.main, 0.05)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
          borderRadius: '20px',
          overflow: 'hidden'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
              '& .MuiTab-root': {
                color: '#94A3B8',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&.Mui-selected': {
                  color: brandColors.primary.light,
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: brandColors.primary.main,
                height: 3
              }
            }}
          >
            <Tab label={t('subscription.plansBilling')} />
            <Tab label={t('subscription.paymentMethods')} />
            <Tab label={t('subscription.invoiceHistory')} />
            <Tab label={t('subscription.settings')} />
          </Tabs>

          {/* Plans & Billing Tab */}
          {activeTab === 0 && (
            <TabPanel>
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                  {t('subscription.availablePlans')}
                </Typography>

                <Grid container spacing={3}>
                  {availablePlans.map((plan) => (
                    <Grid item xs={12} md={4} key={plan.id}>
                      <PlanCard
                        featured={plan.popular}
                        current={plan.id === currentSubscription.planId}
                      >
                        {plan.popular && (
                          <Chip
                            label={t('subscription.mostPopular')}
                            sx={{
                              position: 'absolute',
                              top: -12,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                              color: 'white',
                              fontWeight: 600,
                              zIndex: 1
                            }}
                          />
                        )}

                        {plan.id === currentSubscription.planId && (
                          <Chip
                            label={t('subscription.currentPlan')}
                            sx={{
                              position: 'absolute',
                              top: -12,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                              color: 'white',
                              fontWeight: 600,
                              zIndex: 1
                            }}
                          />
                        )}

                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                            {t(`subscription.plan.${plan.id}`)}
                          </Typography>
                          <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                            ${plan.price}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 4 }}>
                            /{t(`subscription.billing.${plan.billing}`)}
                          </Typography>

                          <Box sx={{ textAlign: 'left', mb: 4 }}>
                            {plan.features.map((featureKey, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Check sx={{ mr: 2, fontSize: 16, color: '#10B981' }} />
                                <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                                  {t(featureKey)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          {plan.id === currentSubscription.planId ? (
                            <Button
                              fullWidth
                              variant="outlined"
                              disabled
                              sx={{
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: brandColors.secondary.main,
                                color: brandColors.secondary.main
                              }}
                            >
                              {t('subscription.currentPlan')}
                            </Button>
                          ) : (
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleUpgrade(plan.id)}
                              sx={{
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontWeight: 600,
                                background: plan.popular
                                  ? `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
                                  : `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                                '&:hover': {
                                  background: plan.popular
                                    ? `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`
                                    : `linear-gradient(135deg, ${brandColors.secondary.light}, ${brandColors.secondary.main})`,
                                }
                              }}
                            >
                              {plan.price === 0 ? 'Downgrade to Free' :
                                plan.price < (currentPlan?.price || 0) ? 'Downgrade' : 'Upgrade'}
                            </Button>
                          )}
                        </CardContent>
                      </PlanCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>
          )}

          {/* Payment Methods Tab */}
          {activeTab === 1 && (
            <TabPanel>
              <Box sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                    {t('subscription.paymentMethods')}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<CreditCard />}
                    onClick={() => setPaymentDialogOpen(true)}
                    sx={{
                      background: `linear-gradient(135deg, ${brandColors.accent.main}, ${brandColors.accent.dark})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${brandColors.accent.light}, ${brandColors.accent.main})`,
                      }
                    }}
                  >
                    {t('subscription.addPaymentMethod')}
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  {paymentMethods.map((method) => (
                    <Grid item xs={12} sm={6} key={method.id}>
                      <Card sx={{
                        background: `linear-gradient(135deg, 
                          ${alpha('#1E293B', 0.8)} 0%, 
                          ${alpha(brandColors.primary.main, 0.1)} 100%)`,
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
                        borderRadius: '16px',
                        position: 'relative'
                      }}>
                        {method.isDefault && (
                          <Chip
                            label={t('subscription.default')}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              background: alpha('#10B981', 0.2),
                              color: '#10B981',
                              fontWeight: 600
                            }}
                          />
                        )}

                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            {method.type === 'card' ? (
                              <CreditCard sx={{ fontSize: 32, color: brandColors.primary.main }} />
                            ) : (
                              <Payment sx={{ fontSize: 32, color: brandColors.accent.main }} />
                            )}

                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                {method.type === 'card'
                                  ? `•••• •••• •••• ${method.last4}`
                                  : t('paypal')
                                }
                              </Typography>
                              {method.type === 'card' && (
                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                  {t(`subscription.brand.${method.brand}`)} • {t('subscription.expires')} {method.expiryMonth}/{method.expiryYear}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {!method.isDefault && (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: brandColors.primary.main,
                                  color: brandColors.primary.main,
                                  textTransform: 'none'
                                }}
                              >
                                {t('subscription.setDefault')}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: '#EF4444',
                                color: '#EF4444',
                                textTransform: 'none'
                              }}
                            >
                              {t('subscription.remove')}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>
          )}

          {/* Invoice History Tab */}
          {activeTab === 2 && (
            <TabPanel>
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                  {t('subscription.invoiceHistory')}
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>{t('subscription.date')}</TableCell>
                        <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>{t('subscription.description')}</TableCell>
                        <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>{t('subscription.amount')}</TableCell>
                        <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>{t('subscription.status')}</TableCell>
                        <TableCell sx={{ color: '#94A3B8', fontWeight: 600 }}>{t('subscription.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell sx={{ color: 'white' }}>
                            {invoice.date.toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ color: 'white' }}>
                            {t('subscription.invoiceDescription', { plan: t(`subscription.plan.${invoice.description}`), billing: t(`subscription.billing.${invoice.currency.toLowerCase()}`) })}
                          </TableCell>
                          <TableCell sx={{ color: 'white' }}>
                            ${invoice.amount.toFixed(2)} {t(`subscription.currency.${invoice.currency.toLowerCase()}`)}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={t(`subscription.status.${invoice.status}`)}
                              status={invoice.status}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={t('subscription.downloadInvoice')}>
                              <IconButton
                                size="small"
                                sx={{ color: brandColors.primary.main }}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TabPanel>
          )}

          {/* Settings Tab */}
          {activeTab === 3 && (
            <TabPanel>
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                  {t('subscription.billingSettings')}
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      background: `linear-gradient(135deg, 
                        ${alpha('#1E293B', 0.8)} 0%, 
                        ${alpha(brandColors.secondary.main, 0.1)} 100%)`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
                      borderRadius: '16px'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                          {t('subscription.notifications')}
                        </Typography>

                        <List sx={{ py: 0 }}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Notifications sx={{ color: brandColors.secondary.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('subscription.paymentReminders')}
                              secondary={t('subscription.getNotifiedBeforeYourNextBillingCycle')}
                              primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                              secondaryTypographyProps={{ color: '#94A3B8' }}
                            />
                            <Switch defaultChecked color="primary" />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Receipt sx={{ color: brandColors.secondary.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('subscription.invoiceEmails')}
                              secondary={t('subscription.receiveInvoiceReceiptsViaEmail')}
                              primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                              secondaryTypographyProps={{ color: '#94A3B8' }}
                            />
                            <Switch defaultChecked color="primary" />
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Warning sx={{ color: brandColors.secondary.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('subscription.paymentFailures')}
                              secondary={t('subscription.alertMeWhenPaymentsFail')}
                              primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                              secondaryTypographyProps={{ color: '#94A3B8' }}
                            />
                            <Switch defaultChecked color="primary" />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      background: `linear-gradient(135deg, 
                        ${alpha('#1E293B', 0.8)} 0%, 
                        ${alpha(brandColors.accent.main, 0.1)} 100%)`,
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(brandColors.accent.main, 0.2)}`,
                      borderRadius: '16px'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 3 }}>
                          {t('subscription.security')}
                        </Typography>

                        <List sx={{ py: 0 }}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Security sx={{ color: brandColors.accent.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('subscription.twoFactorAuthentication')}
                              secondary={t('subscription.secureYourAccountWith2fa')}
                              primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                              secondaryTypographyProps={{ color: '#94A3B8' }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: brandColors.accent.main,
                                color: brandColors.accent.main,
                                textTransform: 'none'
                              }}
                            >
                              {t('subscription.enable')}
                            </Button>
                          </ListItem>

                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon>
                              <History sx={{ color: brandColors.accent.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={t('subscription.loginHistory')}
                              secondary={t('subscription.viewRecentAccountAccess')}
                              primaryTypographyProps={{ color: 'white', fontWeight: 500 }}
                              secondaryTypographyProps={{ color: '#94A3B8' }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: brandColors.accent.main,
                                color: brandColors.accent.main,
                                textTransform: 'none'
                              }}
                            >
                              {t('subscription.view')}
                            </Button>
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          )}
        </Paper>

        {/* Upgrade Dialog */}
        <Dialog
          open={upgradeDialogOpen}
          onClose={() => setUpgradeDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.primary.main, 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
              borderRadius: '20px',
              color: 'white'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Upgrade sx={{ fontSize: 48, color: brandColors.primary.main, mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {t('subscription.upgradeYourPlan')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8' }}>
              {t('subscription.unlockMoreCosmicInsightsAndFeatures')}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {availablePlans.filter(plan => plan.id !== 'free' && plan.id !== currentSubscription.planId).map((plan) => (
                <Grid item xs={12} sm={6} key={plan.id}>
                  <PlanCard featured={plan.popular}>
                    {plan.popular && (
                      <Chip
                        label={t('subscription.recommended')}
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        {t(`subscription.plan.${plan.id}`)}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                        ${plan.price}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                        /{t('subscription.month')}
                      </Typography>

                      <Box sx={{ textAlign: 'left', mb: 3, minHeight: 120 }}>
                        {plan.features.slice(0, 4).map((featureKey, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Check sx={{ mr: 2, fontSize: 16, color: '#10B981' }} />
                            <Typography variant="body2" sx={{ color: '#CBD5E1' }}>
                              {t(featureKey)}
                            </Typography>
                          </Box>
                        ))}
                        {plan.features.length > 4 && (
                          <Typography variant="body2" sx={{ color: '#94A3B8', fontStyle: 'italic' }}>
                            +{plan.features.length - 4} {t('subscription.moreFeatures')}...
                          </Typography>
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleUpgrade(plan.id)}
                        sx={{
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          background: plan.popular
                            ? `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
                            : `linear-gradient(135deg, ${brandColors.secondary.main}, ${brandColors.secondary.dark})`,
                          '&:hover': {
                            background: plan.popular
                              ? `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`
                              : `linear-gradient(135deg, ${brandColors.secondary.light}, ${brandColors.secondary.main})`,
                          }
                        }}
                      >
                        {t('subscription.upgradeTo')} {t(`subscription.plan.${plan.id}`)}
                      </Button>
                    </CardContent>
                  </PlanCard>
                </Grid>
              ))}
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setUpgradeDialogOpen(false)}
              sx={{ color: '#94A3B8' }}
            >
              {t('subscription.cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha('#EF4444', 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha('#EF4444', 0.2)}`,
              borderRadius: '20px',
              color: 'white'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Cancel sx={{ fontSize: 48, color: '#EF4444', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {t('subscription.cancelSubscription')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8' }}>
              {t('subscription.areYouSureYouWantToCancelYourSubscription')}
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                background: alpha('#F59E0B', 0.1),
                border: `1px solid ${alpha('#F59E0B', 0.3)}`,
                '& .MuiAlert-icon': { color: '#F59E0B' },
                '& .MuiAlert-message': { color: '#FCD34D' }
              }}
            >
              {t('subscription.youllContinueToHaveAccessToProFeaturesUntil')} {currentSubscription.currentPeriodEnd.toLocaleDateString()}.
            </Alert>

            <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 2 }}>
              {t('subscription.beforeYouGoHerereWhatYoullLose')}
            </Typography>

            <List sx={{ py: 0 }}>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Close sx={{ color: '#EF4444', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('subscription.unlimitedAiConversations')}
                  primaryTypographyProps={{ color: '#CBD5E1', variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Close sx={{ color: '#EF4444', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('subscription.detailedBirthChartAnalysis')}
                  primaryTypographyProps={{ color: '#CBD5E1', variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <Close sx={{ color: '#EF4444', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('subscription.premiumCompatibilityReports')}
                  primaryTypographyProps={{ color: '#CBD5E1', variant: 'body2' }}
                />
              </ListItem>
            </List>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setCancelDialogOpen(false)}
              variant="outlined"
              sx={{
                borderColor: '#94A3B8',
                color: '#94A3B8'
              }}
            >
              {t('subscription.keepSubscription')}
            </Button>
            <Button
              onClick={handleCancelSubscription}
              variant="contained"
              sx={{
                background: '#EF4444',
                '&:hover': {
                  background: '#DC2626'
                }
              }}
            >
              {t('subscription.cancelSubscription')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Payment Method Dialog */}
        <Dialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.accent.main, 0.1)})`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(brandColors.accent.main, 0.2)}`,
              borderRadius: '20px',
              color: 'white'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <CreditCard sx={{ fontSize: 48, color: brandColors.accent.main, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {t('subscription.addPaymentMethod')}
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('subscription.cardNumber')}
                  placeholder={t('subscription.placeholder.cardNumber')}
                  inputProps={{
                    style: {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: alpha('#0F172A', 0.6),
                      '& fieldset': { borderColor: alpha(brandColors.accent.main, 0.3) },
                      '&:hover fieldset': { borderColor: brandColors.accent.main },
                      '&.Mui-focused fieldset': { borderColor: brandColors.accent.light },
                    },
                    '& .MuiInputLabel-root': { color: '#CBD5E1' },
                    '& .MuiInputBase-input': { color: 'white' },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('subscription.expiryDate')}
                  placeholder={t('subscription.placeholder.expiryDate')}
                  inputProps={{
                    style: {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: alpha('#0F172A', 0.6),
                      '& fieldset': { borderColor: alpha(brandColors.accent.main, 0.3) },
                      '&:hover fieldset': { borderColor: brandColors.accent.main },
                      '&.Mui-focused fieldset': { borderColor: brandColors.accent.light },
                    },
                    '& .MuiInputLabel-root': { color: '#CBD5E1' },
                    '& .MuiInputBase-input': { color: 'white' },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={t('subscription.cvc')}
                  placeholder={t('subscription.placeholder.cvc')}
                  inputProps={{
                    style: {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: alpha('#0F172A', 0.6),
                      '& fieldset': { borderColor: alpha(brandColors.accent.main, 0.3) },
                      '&:hover fieldset': { borderColor: brandColors.accent.main },
                      '&.Mui-focused fieldset': { borderColor: brandColors.accent.light },
                    },
                    '& .MuiInputLabel-root': { color: '#CBD5E1' },
                    '& .MuiInputBase-input': { color: 'white' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('subscription.cardholderName')}
                  placeholder={t('subscription.placeholder.cardholderName')}
                  inputProps={{
                    style: {
                      textAlign: isRTL ? 'right' : 'left'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: alpha('#0F172A', 0.6),
                      '& fieldset': { borderColor: alpha(brandColors.accent.main, 0.3) },
                      '&:hover fieldset': { borderColor: brandColors.accent.main },
                      '&.Mui-focused fieldset': { borderColor: brandColors.accent.light },
                    },
                    '& .MuiInputLabel-root': { color: '#CBD5E1' },
                    '& .MuiInputBase-input': { color: 'white' },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      defaultChecked
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: brandColors.accent.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: brandColors.accent.main,
                        },
                      }}
                    />
                  }
                  label={t('subscription.setAsDefaultPaymentMethod')}
                  sx={{ color: '#CBD5E1' }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={() => setPaymentDialogOpen(false)}
              sx={{ color: '#94A3B8' }}
            >
              {t('subscription.cancel')}
            </Button>
            <Button
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, ${brandColors.accent.main}, ${brandColors.accent.dark})`,
                borderRadius: '12px',
                px: 3,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: `linear-gradient(135deg, ${brandColors.accent.light}, ${brandColors.accent.main})`,
                }
              }}
              onClick={() => {
                setPaymentDialogOpen(false);
                setSnackbarMessage(t('subscription.paymentMethodAddedSuccessfully'));
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
              }}
            >
              {t('subscription.addPaymentMethod')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{
              background: alpha('#1E293B', 0.9),
              border: `1px solid ${alpha(brandColors.primary.main, 0.3)}`,
              backdropFilter: 'blur(20px)',
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success' ? '#10B981' :
                  snackbarSeverity === 'warning' ? '#F59E0B' :
                    snackbarSeverity === 'error' ? '#EF4444' : brandColors.primary.main
              },
              '& .MuiAlert-message': { color: 'white' }
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </SubscriptionContainer>
  );
};

export default SubscriptionManagement;